import { Alert, Box } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { Dispatch, FunctionComponent, SetStateAction, useMemo } from 'react';
import useCustomDispatch from 'redux/dispatch';
import { CustomDataGrid } from '../../../components/datagrid/Custom';
import { Button } from '@nps.today/component-library';
import { DataGridKey } from 'redux/slices/datagrid';
import { getIvrMapToTest } from 'redux/slices/ivr';
import { RootState, useSelector } from 'redux/store';
import { getIvrTestUrl } from '../util/getIvrTestUrl';
import { IvrMap, IVRType } from '../../../@types/ivr';
import { useTranslator } from 'translation/useTranslator';

interface IVRListProps {
  ivrData: IvrMap[];
  loading: boolean;
  selectionModel: GridRowSelectionModel;
  setSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
  setIvrIdToEdit: Dispatch<SetStateAction<number | null>>;
}

export default function IVRList(props: IVRListProps) {
  const translator = useTranslator();
  const { ivrTypes } = useSelector((state: RootState) => state.ivr);
  const customDispatch = useCustomDispatch();
  const DATA_GRID_KEY = DataGridKey.IVRList;

  const columns: GridColDef<IvrMap>[] = useMemo(
    () => [
      {
        field: 'id'
      },
      {
        field: 'name',
        hide: false,
        headerName: translator.name(),
        flex: 1
      },
      {
        field: 'providerName',
        hide: false,
        headerName: translator.ivr(),
        valueGetter: (_value, row) => row.provider?.name ?? '',
        flex: 1
      },
      {
        field: 'queueId',
        hide: false,
        headerName: translator.queueId(),
        flex: 1
      },
      {
        field: 'campaignId',

        headerName: translator.campaignId(),
        flex: 1
      },
      {
        field: 'campaignName',
        hide: false,
        headerName: translator.campaignName(),
        valueGetter: (_value, row) => row.campaign?.name ?? '',
        flex: 1
      },
      {
        field: 'test',
        headerName: translator.test(),
        renderHeader: () => '',
        width: 60,
        hideable: false,
        editable: false,
        align: 'center',
        hideSortIcons: true,
        disableColumnMenu: true,
        filterable: false,
        disableExport: true,
        renderCell: (cellValues) => {
          const ivrName = cellValues.row.provider?.name ?? '';
          const isIvrTestEnabled = Boolean(getIvrTestUrl({ ivrName, ivrTypes }));

          return (
            <>
              {isIvrTestEnabled && (
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <Button
                    dataTestId="ivr-list-test"
                    onClick={() => {
                      customDispatch({
                        action: getIvrMapToTest,
                        actionParameters: cellValues.row.id,
                        disableSuccessMessage: true
                      });
                    }}
                  >
                    Test
                  </Button>
                </div>
              )}
            </>
          );
        }
      }
    ],
    [customDispatch, ivrTypes, translator]
  );

  const duplicateIvrGroups = useMemo(() => {
    const groupedByTypeAndQueueId: Record<string, IvrMap[]> = {};
    props.ivrData.forEach((ivr) => {
      if (!ivr.queueId) {
        return;
      }
      const key = `${ivr.ivrId}-${ivr.queueId}`;
      if (!groupedByTypeAndQueueId[key]) {
        groupedByTypeAndQueueId[key] = [];
      }
      groupedByTypeAndQueueId[key].push(ivr);
    });
    return Object.values(groupedByTypeAndQueueId).filter((group) => group.length > 1);
  }, [props.ivrData]);

  return (
    <Box>
      {duplicateIvrGroups.length > 0 && (
        <DuplicateIvrWarning duplicateIvrGroups={duplicateIvrGroups} ivrTypes={ivrTypes} />
      )}
      <CustomDataGrid
        dataGridKey={DATA_GRID_KEY}
        onRowClick={(row) => props.setIvrIdToEdit(row.id as number)}
        autoHeight
        loading={props.loading}
        checkboxSelection
        disableRowSelectionOnClick
        rows={props.ivrData}
        columns={columns}
        pagination
        onRowSelectionModelChange={(newSelectionModel) => {
          props.setSelectionModel(newSelectionModel);
        }}
        rowSelectionModel={props.selectionModel}
      />
    </Box>
  );
}

interface DuplicateIvrWarningProps {
  duplicateIvrGroups: IvrMap[][];
  ivrTypes: IVRType[];
}
const DuplicateIvrWarning: FunctionComponent<DuplicateIvrWarningProps> = ({
  duplicateIvrGroups,
  ivrTypes
}) => {
  const translator = useTranslator();
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {translator.youHaveMultipleIVRSetupsWithSameProviderAndQueueId()}
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {duplicateIvrGroups.map((group) => {
          const { ivrId, queueId } = group[0];
          const ivrTypeName = ivrTypes?.find((type) => type.id === ivrId)?.name ?? ivrId;
          return (
            <li key={`${ivrId}-${queueId}`}>
              <b>{translator.ivrProviderQueueIdCombination(`${ivrTypeName}`, queueId)}</b>
              <ul style={{ paddingLeft: 20 }}>
                {group.map((ivr) => (
                  <li key={ivr.id}>{ivr.name ?? translator.none()}</li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </Alert>
  );
};
